import { Fragment, useState } from 'react';
import { FlatList, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useDispatch, useSelector, useStore } from 'react-redux';
// @ts-ignore: Unable to find module error
import { getEnvValue } from '../../../Helper/env';
import { useNavigation } from '@react-navigation/native';
import { Icon, Text } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashFilter from 'lodash/filter';
import lodashGet from 'lodash/get';
import lodashIsEmpty from 'lodash/isEmpty';
import UAParser from 'ua-parser-js';

import MealPlanList from '../../../Components/Sheets/MealPlanList/Render';
import constants from '../../../Config/constants';
import messages from '../../../Config/messages';
import CheckoutHelper from '../../../Helper/Checkout';
import { getCartTerm } from '../../../Helper/RemoteConfig';
import Sentry from '../../../Helper/Sentry';
import StoreHelper from '../../../Helper/Store';
import useCart from '../../../Hooks/useCart';
import useError from '../../../Hooks/useError';
import useMealPlan from '../../../Hooks/useMealPlan';
import routeList from '../../../Routes/list';
import { removeCart } from '../../../RTK/cart';
import { selectCart } from '../../../RTK/cart/selectors';
import {
  removeCheckoutDetails,
  updateCheckoutDetails,
} from '../../../RTK/checkout';
import {
  checkoutOrderTypeSelector,
  checkoutPromoSelector,
  checkoutStorePromoSelector,
} from '../../../RTK/checkout/selectors';
import { checkout } from '../../../RTK/defaultValues';
import { whenFilterSelector } from '../../../RTK/filter/selectors';
import { shopSelector } from '../../../RTK/shop/selectors';
import {
  selectPage,
  successfulOrderSelector,
} from '../../../RTK/utility/selectors';
import checkoutApi from '../../../Service/api/checkout';
import userApi from '../../../Service/api/user';
import ThemeColor from '../../../Theme/colors';
import ThemeStyle from '../../../Theme/styles';

import Badge from '../../Badge';
import Button from '../../Button';
import EmptyData from '../../EmptyData';
import { RectangleLoader } from '../../Loader';
import OngoingOrderBanner from '../../OngoingOrderBanner';

import { MODALPROMPT } from '../Modal/ModalPrompt/config';
import useModalPrompt from '../Modal/ModalPrompt/hooks/useModalPrompt';

import CartListService from './CartListService';

export function isMobileSafari() {
  const { browser, os } = UAParser();
  return (
    ['Safari Mobile', 'Mobile Safari', 'Facebook'].includes(browser.name) &&
    ['iOS'].includes(os.name)
  );
}

const { ORDER_TYPES } = constants;
const RAZZLE_BUILD_MODE = getEnvValue('RAZZLE_BUILD_MODE');

const Cart = (props) => {
  const { isMealPlanAndHasType } = useMealPlan();
  const page = useSelector(selectPage);
  if (page.isStore && isMealPlanAndHasType) return <MealPlanList />;
  return <CartList {...props} />;
};

const CartList = ({
  isCartLoading = false,
  isClickableHeader = true,
  emptyCartTitle = '',
  emptyCartSubTitle = '',
  onItemPress,
}: any) => {
  const navigation = useNavigation<any>();
  const dispatch = useDispatch();
  // this list renders a cart per store, so the checkout details are read on press for the store being checked out instead of subscribing to a single store here
  const reduxStore = useStore();
  const shopData = useSelector(shopSelector);
  const cartData = useSelector((state) => selectCart(state, shopData?.id));

  const whenFilter = useSelector(whenFilterSelector);
  const successfulOrder = useSelector(successfulOrderSelector);
  const { ErrorCartOrCheckoutHandler } = useError(shopData?.id);
  const { addOrUpdateCart, removeThisProductOnCart } = useCart();
  const { showModalPrompt } = useModalPrompt();

  const [cartItemUpdatingById, setCartItemUpdatingById] = useState<string>('');

  // Branded loader state
  const [isPaymentLinkLoading, setPaymentLinkLoaderStatus] = useState(false);

  const cartTerm = getCartTerm();

  const goBackToStore = (storeId) => (e) => {
    e?.preventDefault();
    navigation.navigate(routeList.STORE, { id: storeId });
  };

  const _onUpdateQuantity = (item) => async () => {
    setCartItemUpdatingById(item.cart_details_id);
    addOrUpdateCart({
      from: 'Product Page',
      item: { ...item, id: item.item_id, quantity: item.quantity },
      orderType: item.order_type,
      store: { id: item.store_id, name: item.store_name },
      onCancel: () => setCartItemUpdatingById(''),
      onError: async (error) => {
        setCartItemUpdatingById('');
        ErrorCartOrCheckoutHandler({
          error: error,
          isAcceptingAdvanceOrder:
            shopData?.is_accepting_in_advanced_orders ||
            item?.is_accepting_in_advanced_orders,
          store_id: item.store_id,
        });
      },
      onSuccess: () => setCartItemUpdatingById(''),
    });
  };
  const _toggleConfirmDeleteModal = (item, type) => () => {
    const isSingleItem = type === 'single';
    const itemNameOrStoreName = isSingleItem
      ? item?.product_name
      : item?.store_name;

    showModalPrompt(MODALPROMPT.prompt, {
      title: 'Are you sure?',
      message: `Do you really want to remove ${itemNameOrStoreName} from your ${cartTerm}?`,
      buttons: [
        {
          size: 'small',
          status: 'danger',
          text: 'Yes',
          onPress: () =>
            isSingleItem ? _removeThisProductOnCart(item) : _removeAll(item),
        },
        { size: 'small', text: 'No' },
      ],
    });
  };
  const _removeThisProductOnCart = async (item: any) => {
    setCartItemUpdatingById(item.cart_details_id);
    removeThisProductOnCart({
      item: item,
      storeId: item.store_id,
      onSuccess: () => setCartItemUpdatingById(''),
      onError: () => () => setCartItemUpdatingById(''),
    });
  };
  const _removeAll = async (item: any) => {
    const handleRemoveAll = async () => {
      await dispatch(removeCart(item.store_id)); // clear store cart data
      await dispatch(removeCheckoutDetails(item.store_id)); // clear store checkout details
      setCartItemUpdatingById('');
    };

    if (RAZZLE_BUILD_MODE !== 'branded') {
      const { ok, data } = await userApi.removeStoreCart([item.store_id]);
      if (ok) {
        // _handleStorageChange()
        await handleRemoveAll();
      } else {
        setCartItemUpdatingById('');
        // show alert
        Sentry.reportError('Error removing store cart', data);
        // @ts-ignore: JS code error
        alert('Please try again', 'Failed to remove store cart');
      }
    } else {
      await handleRemoveAll();
    }
  };

  const _getItemNames = (cartItems, itemIds) => {
    return lodashFilter(cartItems, ({ item_id }) => itemIds.includes(item_id))
      .map((i) => i.name)
      .join(', ');
  };

  const _goToCheckout = async (
    storeInfo,
    availableResult,
    storeCart,
    onCheckoutError,
  ) => {
    const state = reduxStore.getState();
    // falls back to the order type on the url param when nothing is selected yet
    const orderType = checkoutOrderTypeSelector(state, storeInfo.id, true);
    const appliedPromo = checkoutPromoSelector(state, storeInfo.id); // promo applied on checkout
    const storePromo = checkoutStorePromoSelector(state, storeInfo.id); // promo applied on store page

    if (availableResult.valid) {
      if (RAZZLE_BUILD_MODE === 'branded') {
        const order_date = lodashGet(whenFilter, 'date');
        const order_time = lodashGet(whenFilter, 'time');
        const order_type = orderType?.value || ORDER_TYPES['DELIVERY'];

        setPaymentLinkLoaderStatus(true);

        const landing_referrer = localStorage.getItem(
          'branded_redirect_referrer',
        );

        const { ok, data } = await checkoutApi.placeOrder({
          items: storeCart.items.map(({ quantity, id, extras }) => ({
            quantity,
            id,
            extras: extras.map(({ id }) => id),
          })),
          store_id: storeInfo.id,
          promo_code:
            appliedPromo?.code?.toUpperCase() || storePromo?.promo_code || '',
          order_type,
          order_date,
          order_time,
          referrer: landing_referrer,
        });

        if (ok) {
          if (data?.success) {
            window?.localStorage?.setItem(
              'BRANDED_LVS',
              window?.location?.href,
            );
            dispatch(
              updateCheckoutDetails({
                store_id: storeInfo.id,
                keyToUpdate: checkout.keys.CONTINUE_TO_PAYMENT,
                keyValue: {
                  checkout_date: dayjs().toISOString(),
                  storeId: storeInfo.id,
                  url: data?.redirect,
                },
              }),
            );
            if (isMobileSafari()) {
              window.open(data?.redirect, '_self');
            } else {
              // checkout webview of branded
              navigation.navigate(routeList.CHECKOUT, {
                storeId: storeInfo.id,
                url: data?.redirect,
              });
            }
            setPaymentLinkLoaderStatus(false);
          } else {
            const errorItemIds = CheckoutHelper.getItemIdsFromError(data);
            const errorItemNames = _getItemNames(storeCart.items, errorItemIds);
            onCheckoutError?.(data);
            ErrorCartOrCheckoutHandler({
              error: {
                title: constants.ERROR_TYPE.UNAVAILABLE,
                itemNames: errorItemNames,
              },
              isAcceptingAdvanceOrder:
                shopData?.is_accepting_in_advanced_orders,
            });
            setPaymentLinkLoaderStatus(false);
          }
        } else {
          let error;
          if (
            constants.STORE_CLOSING_OR_CLOSED_ERROR[0].includes(
              data?.message?.toLowerCase(),
            )
          ) {
            error = {
              title: messages.STORE_CLOSED_MOBILE.title,
              message: messages.STORE_CLOSED_MOBILE.message,
            };
          } else if (
            constants.STORE_CLOSING_OR_CLOSED_ERROR[1].includes(
              data?.message?.toLowerCase(),
            )
          ) {
            error = {
              title: messages.STORE_CLOSING.title,
              message: messages.STORE_CLOSING.message,
            };
          } else {
            error = {
              title: messages.COMMON_ERROR_TITLE,
              message: messages.COMMON_ERROR_MESSAGE,
            };
          }
          ErrorCartOrCheckoutHandler({
            error,
            isAcceptingAdvanceOrder: shopData?.is_accepting_in_advanced_orders,
          });
          setPaymentLinkLoaderStatus(false);
        }
      } else {
        navigation.navigate(routeList.CHECKOUT);
      }
    } else {
      const errorItemNames = _getItemNames(
        storeCart.items,
        availableResult.unavailable_items,
      );
      ErrorCartOrCheckoutHandler({
        error: {
          title: constants.ERROR_TYPE.UNAVAILABLE,
          itemNames: errorItemNames,
        },
        isAcceptingAdvanceOrder: shopData?.is_accepting_in_advanced_orders,
      });
      setPaymentLinkLoaderStatus(false);
    }
  };

  const storeHeader = (item) =>
    !isCartLoading ? (
      <View style={ThemeStyle.spacingTopSmall}>
        <Text category="p1" status="primary">
          Your {cartTerm} from
        </Text>
        <View style={ThemeStyle.flexDirectionRowCenter}>
          {!isClickableHeader ? (
            <View
              style={[
                ThemeStyle.flex1,
                ThemeStyle.spacingRightMedium,
                styles.storeContainer,
              ]}
            >
              <Text category="p1" numberOfLines={2}>
                {item.store_name}
              </Text>
            </View>
          ) : (
            <Button
              onPress={goBackToStore(item.store_id)}
              style={ThemeStyle.flex1}
              accessibilityRole="link"
              href={`/store?id=${item.store_id}`}
              target="_blank"
              plain
            >
              <View
                style={[
                  ThemeStyle.flex1,
                  ThemeStyle.spacingRightMedium,
                  styles.storeContainer,
                ]}
              >
                <Text category="p1" numberOfLines={2}>
                  {item.store_name}
                </Text>

                <Icon
                  name="chevron-right"
                  fill={ThemeColor.black}
                  style={styles.iconSize}
                />
              </View>
            </Button>
          )}
          {StoreHelper.isPaymentPending({
            status: item.status,
            storeId: item.store_id,
            successfulOrder,
          }) ? (
            //@ts-ignore: Error redline due to JS File
            <Badge
              category={Badge.CATEGORIES.WARNING}
              type={Badge.TYPES.OUTLINE}
              size="c1"
            >
              {messages.PAYMENT_PENDING.title}
            </Badge>
          ) : (
            <Button
              onPress={_toggleConfirmDeleteModal(item, 'all')}
              disabled={Boolean(cartItemUpdatingById)}
              plain
            >
              <View style={styles.itemRemoveButton}>
                <Text category="c1" appearance="alternative">
                  Remove
                </Text>
              </View>
            </Button>
          )}
        </View>
      </View>
    ) : (
      <View>
        <View style={ThemeStyle.spacingTopMedium}>
          <RectangleLoader width={100} height={20} />
        </View>
        <View style={ThemeStyle.spacingTopMedium}>
          <RectangleLoader width={100} height={20} />
        </View>
      </View>
    );

  const _renderItem = ({ item, index }) => (
    <CartListService
      key={index}
      isCartLoading={isCartLoading}
      store_cart={item}
      goToCheckout={_goToCheckout}
      onUpdateQuantity={_onUpdateQuantity}
      storeHeader={storeHeader}
      toggleConfirmDeleteModal={_toggleConfirmDeleteModal}
      onItemPress={onItemPress}
      isUpdatingItem={cartItemUpdatingById}
      isPaymentLinkLoading={isPaymentLinkLoading}
    />
  );

  const contentContainerStyle: StyleProp<ViewStyle> = [{ paddingBottom: 100 }];

  if (lodashIsEmpty(cartData))
    contentContainerStyle.unshift(styles.cartContentCenter);

  return (
    <Fragment>
      <FlatList
        data={cartData}
        renderItem={_renderItem}
        ListEmptyComponent={
          <EmptyData
            type="cart"
            title={emptyCartTitle}
            subtitle={emptyCartSubTitle}
          />
        }
        ItemSeparatorComponent={() => <View style={styles.storeSeparator} />}
        ListHeaderComponent={
          //@ts-ignore: Error redline due to JS File
          RAZZLE_BUILD_MODE !== 'branded' ? <OngoingOrderBanner /> : null
        }
        ListHeaderComponentStyle={
          lodashIsEmpty(cartData) && { marginBottom: 40 }
        }
        contentContainerStyle={contentContainerStyle}
        style={ThemeStyle.spacingTopMedium}
      />
    </Fragment>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    ...ThemeStyle.spacingTopMedium,
    ...ThemeStyle.spacingBottomMedium,
  },
  selectionContainer: {
    ...ThemeStyle.pageHorizontalSpacing,
    ...ThemeStyle.pageVerticalSpacing,
    backgroundColor: ThemeColor.gray,
    borderRadius: 10,
  },
  active: {
    backgroundColor: ThemeColor.green,
  },
  icon: {
    ...ThemeStyle.spacingRightMedium,
    width: 20,
    height: 20,
  },
  storeSeparator: { height: 10 },
  storeContainer: {
    justifyContent: 'flex-start',
    flexDirection: 'row',
  },

  iconSize: { width: 20, height: 20 },
  itemRemoveButton: {
    ...ThemeStyle.pageVerticalSpacingSmall,
    ...ThemeStyle.pageHorizontalSpacingSmall,
    backgroundColor: ThemeColor.red,
    borderRadius: 10,
  },
  box: {
    borderWidth: 1,
    borderColor: ThemeColor.gray,
    borderRadius: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
    padding: 5,
  },
  cartContentCenter: {
    flexGrow: 1,
    justifyContent: 'center',
    marginBottom: 50,
  },
});

export default Cart;
